import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "~/lib/ui/utils/utils";

const ToastProvider = ToastPrimitives.Provider;

function ToastViewport({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      // Radix's modal Dialog aria-hides everything outside itself via
      // `hideOthers`, which exempts only `[aria-live]` elements. Without this
      // marker, any toast shown while a modal dialog is open — including the
      // superadmin "Confirm write" action the held mutation depends on — is
      // stripped from the accessibility tree. "off" keeps Radix's internal
      // per-toast announcer as the single source of screen-reader
      // announcements.
      aria-live="off"
      className={cn(
        `
          pointer-events-none fixed right-0 bottom-0 z-100 flex max-h-screen
          w-full flex-col p-4

          md:w-fit md:min-w-[420px] md:max-w-[calc(100vw-2rem)]
        `,
        className,
      )}
      {...props}
    />
  );
}

const toastVariants = cva(
  `
    group pointer-events-auto relative flex w-full items-center justify-between
    space-x-4 overflow-hidden rounded-xl border border-subtle p-6 pr-8 shadow-lg
    transition-all

    data-[state=closed]:animate-out data-[state=closed]:fade-out-80
    data-[state=closed]:slide-out-to-right-full

    data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full

    data-[swipe=cancel]:translate-x-0

    data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x)
    data-[swipe=end]:animate-out

    data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)
    data-[swipe=move]:transition-none
  `,
  {
    defaultVariants: {
      variant: "info",
    },
    variants: {
      variant: {
        destructive:
          "group border border-l-4 border-l-detail-failure bg-background text-foreground",
        info: "border bg-background text-foreground",
        success:
          "border border-l-4 border-l-detail-success bg-background text-foreground",
        warning:
          "border border-l-4 border-l-detail-warning bg-background text-foreground",
      },
    },
  },
);

function Toast({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>) {
  return (
    <ToastPrimitives.Root
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
}

function ToastClose({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Close>) {
  return (
    <ToastPrimitives.Close
      className={cn(
        `
          absolute right-2 top-2 rounded-md p-1 opacity-70
          transition-opacity

          focus-visible:opacity-100 focus-visible:outline-hidden
          focus-visible:ring-2

          group-hover:opacity-100

          hover:opacity-100
        `,
        className,
        "text-muted-foreground hover:text-foreground",
      )}
      toast-close=""
      {...props}
    >
      <X className="h-4 w-4" />
    </ToastPrimitives.Close>
  );
}

function ToastAction({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Action>) {
  return (
    <ToastPrimitives.Action
      className={cn(
        `
          inline-flex h-8 shrink-0 cursor-pointer items-center justify-center
          rounded-md border bg-transparent px-3 text-sm font-medium
          transition-colors

          hover:bg-secondary

          focus:ring-1 focus:outline-none

          disabled:pointer-events-none disabled:opacity-50
        `,
        className,
      )}
      {...props}
    />
  );
}

function ToastTitle({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Title>) {
  return (
    <ToastPrimitives.Title
      className={cn("text-sm font-semibold", className)}
      {...props}
    />
  );
}

function ToastDescription({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Description>) {
  return (
    <ToastPrimitives.Description
      className={cn("text-sm opacity-90", className)}
      {...props}
    />
  );
}

type ToastProps = React.ComponentProps<typeof Toast>;

type ToastActionElement = React.ReactElement<
  React.ComponentProps<typeof ToastPrimitives.Action>
>;

export {
  ToastAction,
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
};
